import React from 'react';
import { useFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  TextField,
} from '@material-ui/core';
import { RemoveRedEye } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import {
  StockMovement,
  StockMovementLocation,
  StockMovementType,
  StockMovementReason,
} from 'types/models/StockMovement';
import MovementStatusChip from 'components/Stock/MovementStatusChip';
import { formatDateTime } from 'utils/datetime';
import { getStockMovements } from 'services/stockMovementsServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import StockDateRangeFilters from 'types/models/StockDateRangeFilters';
import { parseDateToFormik, parseDateTimeLumen } from 'utils/datetime';
import useSetupInitialState from 'hooks/useSetupInitialState';
import Venue from 'types/models/Venue';
import moment from 'moment';
import CopyEmail from 'components/common/CopyEmail';
import SelectField from 'components/SelectField';
import { sumStockUnits, sumLossValue } from 'utils/stockModule';
import { formatMoneyAuto } from 'utils/formatMoney';
import { mapToSelectOptions } from 'utils/select';
import UserRoleType from 'types/enums/UserRoleType';

const TYPE_LABEL: Record<StockMovementType, string> = {
  [StockMovementType.ADJUSTMENT]: 'stock.movements.types.ADJUSTMENT',
  [StockMovementType.RECEIPT]: 'stock.movements.types.RECEIPT',
  [StockMovementType.TRANSFER]: 'stock.movements.types.TRANSFER',
  [StockMovementType.LOSS]: 'stock.movements.types.LOSS',
  [StockMovementType.SALE]: 'stock.movements.types.SALE',
  [StockMovementType.PRODUCTION]: 'stock.movements.types.PRODUCTION',
};

const MOVEMENT_TYPE_OPTIONS = [StockMovementType.RECEIPT, StockMovementType.TRANSFER, StockMovementType.ADJUSTMENT];

const DEFAULT_TYPES = MOVEMENT_TYPE_OPTIONS.join(',');

export default (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const entity = useSetupInitialState();
  const venueTimeZone = (entity as Venue | null)?.timeZoneIdentifier ?? '';

  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const { setSelectedMovement } = React.useContext(EditStockModuleContext);
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockMovement>>();
  const [venues, setVenues] = React.useState<Venue[] | null>(null);

  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const typeFilterOptions = MOVEMENT_TYPE_OPTIONS.map((type) => ({
    label: t(TYPE_LABEL[type] ?? type),
    value: type,
  }));

  const reasonFilterOptions = [
    StockMovementReason.DAMAGED,
    StockMovementReason.EXPIRED,
    StockMovementReason.INVENTORY_COUNT,
    StockMovementReason.MANUAL_ADJUSTMENT,
  ].map((reason) => ({
    label: t(`stock.loss-reasons.${reason}`),
    value: reason,
  }));
  const [defaultRange] = React.useState(() => ({
    from: moment().subtract(7, 'days').toDate(),
    to: moment().add(1, 'day').endOf('day').toDate(),
  }));

  const dateFiltersRef = React.useRef<StockDateRangeFilters>({
    dateFrom: parseDateTimeLumen(defaultRange.from, venueTimeZone),
    dateTo: parseDateTimeLumen(defaultRange.to, venueTimeZone),
    localTimeZone: venueTimeZone,
  });

  const formInitialValues = {
    dateFrom: parseDateToFormik(defaultRange.from, venueTimeZone),
    dateTo: parseDateToFormik(defaultRange.to, venueTimeZone),
  };

  const formik = useFormik({
    initialValues: {
      dateFrom: formInitialValues.dateFrom,
      dateTo: formInitialValues.dateTo,
      movementFilter: [] as string[],
      venueId: '',
    },
    onSubmit: (values) => {
      dateFiltersRef.current = {
        dateFrom: values.dateFrom ? parseDateTimeLumen(moment(values.dateFrom).toDate(), venueTimeZone) : undefined,
        dateTo: values.dateTo ? parseDateTimeLumen(moment(values.dateTo).toDate(), venueTimeZone) : undefined,
        localTimeZone: venueTimeZone,
      };
      loadData(undefined);
    },
  });

  const movementFilter = formik.values.movementFilter;

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    formik.setFieldValue('venueId', entity.id);
  }, [isVenueEditor, entity]);

  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isLossMovementsPage = searchParams.get('type') === StockMovementType.LOSS;
  const filteredGlobalProductId = searchParams.get('globalProductId') || undefined;
  const filteredProductName = searchParams.get('productName') || undefined;

  const loadData = React.useCallback(
    async (page?: number, searchTerm?: string) => {
      try {
        const { dateFrom, dateTo, localTimeZone } = dateFiltersRef.current;
        const typeFilter = isLossMovementsPage
          ? StockMovementType.LOSS
          : movementFilter.length > 0
          ? movementFilter.join(',')
          : DEFAULT_TYPES;
        const reasonFilter = isLossMovementsPage && movementFilter.length > 0 ? movementFilter.join(',') : undefined;
        const paginationData = await getStockMovements(page, searchTerm, {
          type: filteredGlobalProductId ? undefined : typeFilter,
          reason: reasonFilter,
          globalProductId: filteredGlobalProductId,
          dateFrom,
          dateTo,
          localTimeZone,
          venueId: formik.values.venueId || undefined,
        });
        if (paginationData) setPaginationData(paginationData);
      } catch (error) {
        console.log(error);
      }
    },
    [isLossMovementsPage, filteredGlobalProductId, movementFilter, formik.values.venueId],
  );

  React.useEffect(() => {
    if (isVenueEditor) return;
    let mounted = true;
    const loadVenues = async () => {
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.log(error);
      }
    };
    loadVenues();
    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!isVenueEditor && !formik.values.venueId) {
      setPaginationData(undefined);
      return;
    }
    loadData(currentPageListNumber ?? undefined);
  }, [currentPageListNumber, location.search, loadData, isVenueEditor, formik.values.venueId]);

  function renderLocation(loc?: StockMovementLocation | null) {
    if (!loc) return '—';

    return (
      <>
        <Chip size="small" variant="outlined" label={t(`stock.location-label.${loc.type}`)} /> · {loc.name}
      </>
    );
  }

  const renderContent = (paginationData: StockMovement[]): JSX.Element => {
    return (
      <>
        {paginationData.map((movement) => (
          <TableRow key={movement.id}>
            <TableCell>{formatDateTime(movement.localDatetime)}</TableCell>
            <TableCell className="unique-value-cell">
              {isLossMovementsPage
                ? t(`stock.loss-reasons.${movement.reason}`)
                : t(TYPE_LABEL[movement.type] ?? movement.type).toUpperCase()}
            </TableCell>
            <TableCell>{renderLocation(movement.source)}</TableCell>
            <TableCell>{renderLocation(movement.destination)}</TableCell>
            <TableCell>
              {movement.items?.length} prod. · {sumStockUnits(movement)} u.
              {isLossMovementsPage &&
                (() => {
                  const lost = sumLossValue(movement);
                  return lost != null ? <> · -{formatMoneyAuto(lost)}</> : null;
                })()}
            </TableCell>
            <TableCell>{movement.createdBy?.name ?? <CopyEmail email={movement.createdBy?.email || ''} />}</TableCell>
            <TableCell>{movement.receivedBy?.name ?? '—'}</TableCell>
            <TableCell>
              <MovementStatusChip status={movement.status} />
            </TableCell>
            <TableCell>{renderViewButton(movement)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  const renderViewButton = (movement: StockMovement): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!movement) return;

          if (movement.type === StockMovementType.RECEIPT) {
            const receptionId = movement.stockReceptionId;
            if (!receptionId) {
              alert('No se encontró la recepción asociada a este movimiento');
              return;
            }

            return history.push(`/private/stock/receptions/${receptionId}/view`);
          }

          setSelectedMovement(movement);
          history.push(`/private/stock/movements/${movement.id}/view?type=${movement.type}`);
        }}
      >
        <RemoveRedEye />
      </IconButton>
    );
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.movements.filters.venue')}
            placeholder={t('stock.movements.filters.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues ?? [], { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isSearchable
            isDisabled={false}
          />
        )}
        <div className="Grid Grid--columns">
          <TextField
            margin="dense"
            fullWidth
            name="dateFrom"
            label={t('stock.movements.filters.dateFrom')}
            value={formik.values.dateFrom}
            onChange={formik.handleChange}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            fullWidth
            name="dateTo"
            label={t('stock.movements.filters.dateTo')}
            value={formik.values.dateTo}
            onChange={formik.handleChange}
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <SelectField
          name="movementFilter"
          label={t(isLossMovementsPage ? 'stock.forms.reason' : 'stock.movements.table.type')}
          placeholder={t('stock.forms.select')}
          value={formik.values.movementFilter}
          options={isLossMovementsPage ? reasonFilterOptions : typeFilterOptions}
          isMulti
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={false}
          isSearchable={false}
          onBlur={() => undefined}
          touched={formik.touched.movementFilter}
          error={formik.errors.movementFilter}
          setFieldValue={(_name: string, val: unknown) => {
            formik.setFieldValue('movementFilter', (val as string[]) ?? []);
            setCurrentPageListNumber(1);
          }}
        />

        <Box display="flex" justifyContent="flex-end" marginTop="1rem">
          <Button className="btn-50" variant="contained" color="primary" type="submit">
            {t('search-label')}
          </Button>
        </Box>
      </form>

      {isLossMovementsPage ? (
        <Button
          className="btn-50 my-6"
          variant="contained"
          color="secondary"
          onClick={() => {
            history.push('/private/stock/movements/loss/create');
          }}
        >
          {t('stock.movement-actions.create-loss')}
        </Button>
      ) : (
        <div>
          <Button
            className="btn-50 my-6"
            variant="contained"
            color="secondary"
            onClick={() => {
              history.push('/private/stock/movements/transfer/create');
            }}
          >
            {t('stock.movement-actions.create-transfer')}
          </Button>
          <Button
            className="btn-50 my-6"
            variant="contained"
            color="secondary"
            onClick={() => {
              history.push('/private/stock/movements/adjustment/create');
            }}
          >
            {t('stock.movement-actions.create-adjustment')}
          </Button>
        </div>
      )}

      {filteredGlobalProductId && (
        <Chip
          className="my-6"
          size="small"
          variant="outlined"
          color="primary"
          label={t('stock.movements.filter-product', {
            product: filteredProductName || filteredGlobalProductId,
          })}
          onDelete={() => history.push('/private/stock/movements')}
        />
      )}

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.movements.table.date')}</TableCell>
              <TableCell>{t(isLossMovementsPage ? 'stock.forms.reason' : 'stock.movements.table.type')}</TableCell>
              <TableCell>{t('stock.movements.table.origin')}</TableCell>
              <TableCell>{t('stock.movements.table.destination')}</TableCell>
              <TableCell>{t('stock.movements.table.products')}</TableCell>
              <TableCell>{t('stock.movements.table.dispatched-by')}</TableCell>
              <TableCell>{t('stock.movements.table.receiver')}</TableCell>
              <TableCell>{t('stock.movements.table.status')}</TableCell>
              <TableCell>{t('stock.movements.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.movements.table.no-results')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
